import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DollarSign, Calendar, Clock, TrendingUp,
  ChevronDown, ChevronRight, Loader2,
} from 'lucide-react';
import { PageHeader, SectionCard, EmptyState, useToast, ToastContainer } from '../components/ui';
import { API_BASE } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_VENDOR_PAYOUTS } from '../demo/data';

type PayoutStatus = 'pending' | 'processing' | 'paid' | 'rejected';

interface PayoutRequest {
  id: number;
  amount: number;
  status: PayoutStatus;
  requested_at: string;
  processed_at: string | null;
  notes: string | null;
}

interface LedgerEntry {
  id: number;
  entry_type: string;
  amount: number;
  description: string;
  reference_type: string;
  reference_id: string;
  created_at: string;
}

interface MonthlyRow {
  month: string;
  earnings: number;
  deductions: number;
  net: number;
}

interface PayoutsData {
  balance: number;
  currency: string;
  next_payout_date: string;
  next_payout_amount: number;
  total_paid: number;
  pending_amount: number;
  payouts: PayoutRequest[];
  ledger: LedgerEntry[];
  monthly_breakdown: MonthlyRow[];
}

const STATUS_COLORS: Record<PayoutStatus, string> = {
  pending: '#eab308',
  processing: '#3b82f6',
  paid: '#4ade80',
  rejected: '#f87171',
};

const TYPE_COLORS: Record<string, string> = {
  sale: '#4ade80',
  commission: '#4ade80',
  refund: '#f87171',
  fee: 'rgba(255,255,255,0.35)',
  adjustment: '#3b82f6',
  payout: '#FFD400',
  chargeback: '#f87171',
};

export default function VendorPayouts() {
  const { t, i18n } = useTranslation(['payouts', 'common']);
  const { isDemoMode } = useDemo();
  const { toasts, addToast, removeToast } = useToast();

  const [data, setData] = useState<PayoutsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reqAmount, setReqAmount] = useState('');
  const [reqNotes, setReqNotes] = useState('');
  const [reqLoading, setReqLoading] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);

  const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  const fmtCur = (n: number) => n.toLocaleString(locale, { style: 'currency', currency: 'USD' });
  const fmtDate = (s: string) => new Date(s).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });

  useEffect(() => {
    if (isDemoMode) {
      setData(DEMO_VENDOR_PAYOUTS as unknown as PayoutsData);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [balRes, payRes, ledRes] = await Promise.all([
          fetch(`${API_BASE}/vendor/financials/balance`, { credentials: 'include' }),
          fetch(`${API_BASE}/vendor/financials/payouts`, { credentials: 'include' }),
          fetch(`${API_BASE}/vendor/financials/ledger`, { credentials: 'include' }),
        ]);
        const bal = balRes.ok ? (await balRes.json()).data : { balance: 0, currency: 'USD' };
        const pays = payRes.ok ? (await payRes.json()).data : [];
        const ledger = ledRes.ok ? (await ledRes.json()).data : [];
        const totalPaid = (pays as PayoutRequest[]).filter((p: PayoutRequest) => p.status === 'paid').reduce((s: number, p: PayoutRequest) => s + p.amount, 0);
        const d = new Date(); d.setDate(15); if (d < new Date()) d.setMonth(d.getMonth() + 1);
        setData({
          balance: bal.balance,
          currency: bal.currency || 'USD',
          next_payout_date: d.toISOString().slice(0, 10),
          next_payout_amount: bal.balance,
          total_paid: totalPaid,
          pending_amount: (pays as PayoutRequest[]).filter((p: PayoutRequest) => p.status === 'pending').reduce((s: number, p: PayoutRequest) => s + p.amount, 0),
          payouts: pays,
          ledger,
          monthly_breakdown: [],
        });
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [isDemoMode]);

  const handleRequestPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(reqAmount);
    if (!amt || amt < 50) { addToast(t('payouts:minAmount', { amount: '50' }), 'error'); return; }
    if (data && amt > data.balance) { addToast(t('payouts:insufficientBalance'), 'error'); return; }
    setReqLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/financials/payouts/request`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt, notes: reqNotes || undefined }),
      });
      if (res.ok) {
        addToast(t('payouts:payoutRequested'), 'success');
        setReqAmount(''); setReqNotes('');
      } else {
        const j = await res.json().catch(() => ({}));
        addToast((j as { error?: string }).error || t('common:error'), 'error');
      }
    } catch { addToast(t('common:error'), 'error'); }
    finally { setReqLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>;
  if (!data) return <EmptyState icon={<DollarSign className="w-8 h-8" />} message={t('payouts:empty')} />;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <PageHeader title={t('payouts:title')} subtitle={t('payouts:subtitle')} />

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: t('payouts:currentBalance'), value: fmtCur(data.balance), icon: DollarSign, color: '#FFD400' },
          { label: t('payouts:nextPayout'), value: fmtCur(data.next_payout_amount), icon: TrendingUp, color: '#4ade80' },
          { label: t('payouts:nextPayoutDate'), value: fmtDate(data.next_payout_date), icon: Calendar, color: '#3b82f6' },
          { label: t('payouts:totalPaid'), value: fmtCur(data.total_paid), icon: Clock, color: '#a78bfa' },
        ].map(c => (
          <div key={c.label} className="p-4" style={{ backgroundColor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' }}>
            <div className="flex items-center gap-2 mb-2">
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
              <span className="text-[11px] text-white/45 uppercase tracking-wider font-medium">{c.label}</span>
            </div>
            <p className="text-lg font-bold text-white">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Request payout */}
      <SectionCard title={t('payouts:requestPayout')}>
        <form onSubmit={handleRequestPayout} className="flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[160px]">
            <label className="text-[11px] text-white/45 uppercase tracking-wider font-medium">{t('payouts:requestAmount')}</label>
            <input type="number" step="0.01" min="50" value={reqAmount} onChange={e => setReqAmount(e.target.value)}
              className="input w-full" placeholder="$0.00" />
          </div>
          <div className="space-y-1 flex-1 min-w-[160px]">
            <label className="text-[11px] text-white/45 uppercase tracking-wider font-medium">{t('payouts:requestNotes')}</label>
            <input type="text" value={reqNotes} onChange={e => setReqNotes(e.target.value)} className="input w-full" />
          </div>
          <button type="submit" disabled={reqLoading || data.balance < 50} className="btn-primary whitespace-nowrap">
            {reqLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('payouts:requestPayout')}
          </button>
        </form>
        <p className="text-[11px] text-white/30 mt-2">{t('payouts:minAmount', { amount: '50' })}</p>
      </SectionCard>

      {/* Payout history */}
      <SectionCard title={t('payouts:payoutHistory')}>
        {data.payouts.length === 0 ? (
          <p className="text-sm text-white/35">{t('payouts:empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>
              <thead>
                <tr className="table-header">
                  <th className="text-left px-3 py-2 text-[11px] text-white/40 uppercase">{t('payouts:date')}</th>
                  <th className="text-right px-3 py-2 text-[11px] text-white/40 uppercase">{t('payouts:amount')}</th>
                  <th className="text-center px-3 py-2 text-[11px] text-white/40 uppercase">{t('common:status')}</th>
                  <th className="text-left px-3 py-2 text-[11px] text-white/40 uppercase">{t('common:notes')}</th>
                </tr>
              </thead>
              <tbody>
                {data.payouts.map(p => (
                  <tr key={p.id} className="table-row">
                    <td className="px-3 py-2.5 text-sm text-white/70">{fmtDate(p.processed_at || p.requested_at)}</td>
                    <td className="px-3 py-2.5 text-sm text-white font-medium text-right">{fmtCur(p.amount)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm"
                        style={{ backgroundColor: `${STATUS_COLORS[p.status]}20`, color: STATUS_COLORS[p.status] }}>
                        {t(`payouts:status.${p.status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-white/35">{p.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Monthly breakdown */}
      {data.monthly_breakdown.length > 0 && (
        <SectionCard title={t('payouts:monthlyBreakdown')}>
          <div className="space-y-1">
            {data.monthly_breakdown.map(m => (
              <div key={m.month}>
                <button type="button" onClick={() => setExpandedMonth(expandedMonth === m.month ? null : m.month)}
                  className="w-full flex items-center justify-between px-3 py-2.5 transition-colors hover:bg-white/[0.02]"
                  style={{ borderRadius: '2px' }}>
                  <div className="flex items-center gap-2">
                    {expandedMonth === m.month ? <ChevronDown className="w-3.5 h-3.5 text-white/30" /> : <ChevronRight className="w-3.5 h-3.5 text-white/30" />}
                    <span className="text-sm text-white/70">{m.month}</span>
                  </div>
                  <span className="text-sm font-medium text-white">{fmtCur(m.net)}</span>
                </button>
                {expandedMonth === m.month && (
                  <div className="px-9 pb-3 flex gap-6 text-xs">
                    <span className="text-[#4ade80]">{t('payouts:earnings')}: {fmtCur(m.earnings)}</span>
                    <span className="text-[#f87171]">{t('payouts:deductions')}: {fmtCur(m.deductions)}</span>
                    <span className="text-white/50">{t('payouts:net')}: {fmtCur(m.net)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Transaction ledger */}
      <SectionCard title={t('payouts:ledger')}>
        {data.ledger.length === 0 ? (
          <p className="text-sm text-white/35">{t('payouts:emptyLedger')}</p>
        ) : (
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '45%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead className="sticky top-0" style={{ backgroundColor: '#0B0B0B' }}>
                <tr className="table-header">
                  <th className="text-left px-3 py-2 text-[11px] text-white/40 uppercase">{t('payouts:date')}</th>
                  <th className="text-left px-3 py-2 text-[11px] text-white/40 uppercase">{t('payouts:type')}</th>
                  <th className="text-left px-3 py-2 text-[11px] text-white/40 uppercase">{t('common:description')}</th>
                  <th className="text-right px-3 py-2 text-[11px] text-white/40 uppercase">{t('payouts:amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.slice(0, 50).map(e => (
                  <tr key={e.id} className="table-row">
                    <td className="px-3 py-2 text-xs text-white/50">{fmtDate(e.created_at)}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
                        style={{ backgroundColor: `${TYPE_COLORS[e.entry_type] || 'rgba(255,255,255,0.1)'}20`, color: TYPE_COLORS[e.entry_type] || 'rgba(255,255,255,0.5)' }}>
                        {t(`payouts:entryTypes.${e.entry_type}`, e.entry_type)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-white/50">{e.description}</td>
                    <td className={`px-3 py-2 text-sm font-medium text-right ${e.amount >= 0 ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>
                      {e.amount >= 0 ? '+' : ''}{fmtCur(e.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
